import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper, InputAdornment, IconButton,
  styled, keyframes, Alert, CircularProgress, Chip,
} from '@mui/material';
import {
  Lock as LockIcon, Groups as TeamIcon,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios, { API, setToken, setUser } from '../api';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: '#101012',
  border: '1px solid #27272a',
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: '0 30px 60px -20px rgba(0, 0, 0, 0.7)',
  maxWidth: 420,
  width: '100%',
  animation: `${fadeIn} 0.5s ease-out`,
}));

const Field = styled(TextField)({
  '& .MuiOutlinedInput-root': { backgroundColor: '#18181b' },
});

// Page publique : l'invité arrive via le lien /invite/<token>, définit son mot
// de passe et rejoint l'équipe (connexion immédiate).
export default function InviteAccept() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null); // { email, owner_email }
  const [invalid, setInvalid] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/team/invite/${token}`);
        setInvite(data);
      } catch (e) {
        setInvalid(e.response?.data?.error || 'Invitation introuvable ou déjà utilisée');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Mot de passe trop court (min. 6 caractères)'); return; }
    if (password !== confirm) { setError('Les mots de passe ne correspondent pas'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/team/invite/${token}/accept`, { password });
      setToken(data.token);
      localStorage.setItem('isAuthenticated', 'true');
      setUser(data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue');
    } finally {
      setSubmitting(false);
    }
  };

  const ownerName = (invite?.owner_email || '').split('@')[0];

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: '#09090b',
      backgroundImage: 'radial-gradient(60% 50% at 50% 0%, rgba(99,102,241,0.12), transparent 70%)',
      p: 2,
    }}>
      <StyledPaper elevation={0}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : invalid ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>Invitation invalide</Typography>
            <Typography sx={{ color: '#a1a1aa', fontSize: '0.9rem', mb: 3 }}>{invalid}</Typography>
            <Button variant="outlined" onClick={() => navigate('/login')} sx={{ textTransform: 'none' }}>
              Aller à la connexion
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box sx={{
                width: 52, height: 52, mx: 'auto', mb: 1.5, borderRadius: 3,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, #6366f1, #4f46e5)', color: '#fff',
              }}>
                <TeamIcon />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', mb: 0.5, textTransform: 'capitalize' }}>
                Rejoindre l'équipe de {ownerName}
              </Typography>
              <Typography sx={{ color: '#a1a1aa', fontSize: '0.9rem' }}>
                Définis ton mot de passe pour activer ton accès
              </Typography>
              <Chip label={invite.email} size="small" sx={{ mt: 1.5, bgcolor: 'rgba(99,102,241,0.14)', color: '#c7d2fe', fontWeight: 600 }} />
            </Box>

            <form onSubmit={handleSubmit}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Field
                fullWidth label="Mot de passe" type={showPassword ? 'text' : 'password'} value={password}
                onChange={(e) => setPassword(e.target.value)} required margin="normal" autoComplete="new-password"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#71717a', fontSize: 20 }} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((s) => !s)} edge="end" sx={{ color: '#71717a' }}>
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Field
                fullWidth label="Confirmer le mot de passe" type={showPassword ? 'text' : 'password'} value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required margin="normal" autoComplete="new-password"
                InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#71717a', fontSize: 20 }} /></InputAdornment> }}
              />

              <Button
                fullWidth type="submit" variant="contained" disabled={submitting}
                sx={{ mt: 2.5, py: 1.25, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
              >
                {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : "Rejoindre l'équipe"}
              </Button>
            </form>
          </>
        )}
      </StyledPaper>
    </Box>
  );
}
